<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <div
    class="min-h-screen bg-[var(--color-bg)] text-[var(--color-text)] p-4 pb-24">
    <!-- LOADING -->
    <Loading v-if="localLoading" :text="$t('views.selectedGoat.loading')" />

    <!-- ERROR -->
    <div v-else-if="error" class="text-red-600 text-center">
      {{ error }}
    </div>

    <!-- CONTENT -->
    <div v-else-if="goat" class="space-y-6">
      <!-- FORM for editing general data -->
      <EditGeneralInfoModule :goat="goat" @saved="refresh" />
    </div>
  </div>
</template>

<script setup>
  import { ref, onMounted, onUnmounted, watch } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { useGoatStore } from '@/stores'
  import { storeToRefs } from 'pinia'
  import { useUiStore } from '@/stores/ui.store'
  import { useI18n } from 'vue-i18n'

  import Loading from '@/components/ui/feedback/Loading.vue'
  import BackButton from '@/components/ui/buttons/GetBackButton.vue'
  import EditGeneralInfoModule from '@/components/goats/EditGeneralInfoModule.vue'

  const route = useRoute()
  const router = useRouter()
  const goatId = Number(route.params.id)

  const goatStore = useGoatStore()
  const ui = useUiStore()

  const { error } = storeToRefs(goatStore)
  const goat = ref(null)
  const localLoading = ref(false)

  const { t } = useI18n()

  onMounted(async () => {
    localLoading.value = true
    goat.value = await goatStore.getGoatById(goatId)
    localLoading.value = false
  })

  // Refresh Header
  watch(goat, (g) => {
    if (!g) return

    ui.setHeader({
      title: t('views.selectedGoat.generalInfo.title'), 
      subtitle: g.eartag || t('views.goats.noEartag'),
      showBack: true, 
    })
  })

  // Navigate back to GoatDetailView after successful save
  function refresh() {
    router.go(-1)
  }
</script>
